// Customer 360 — landing page for a single customer at /customers/$id.
//
// Adapted from the "Customer 360.html" design-handoff bundle (AB#11889).
// Sections requiring data the backend doesn't carry yet (revenue/invoicing,
// engagement timeline, cross-source activity feed, sales opportunities linked
// to customer, map coordinates, NPS) are omitted rather than mocked — they
// have separate tech-debt work items linked to the parent story.

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  ArrowLeft, Calendar, Users as UsersIcon, Mail, Phone, MessageSquare,
  Globe, MapPin, ExternalLink, Pencil, Plus, FileText, ShieldCheck,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import type {
  Customer, CustomerDetail, Contact, Project, Agreement,
} from '@/types/api'
import './customerOverview.css'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function hueFor(name: string): number {
  let h = 0
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0
  return h % 360
}

function avatarStyle(name: string): React.CSSProperties {
  const h = hueFor(name)
  return {
    background: `linear-gradient(135deg, oklch(0.6 0.14 ${h}), oklch(0.5 0.16 ${(h + 60) % 360}))`,
  }
}

function customerLogoStyle(name: string): React.CSSProperties {
  // Slightly more saturated than the contact avatars so the customer feels brand-ish.
  const h = hueFor(name)
  return {
    background: `linear-gradient(135deg, oklch(0.7 0.16 ${h}), oklch(0.55 0.18 ${(h + 30) % 360}))`,
  }
}

function fmtMonthYear(s: string | null | undefined): string {
  if (!s) return '—'
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—'
  const d = new Date(s.length === 10 ? s + 'T00:00:00' : s)
  if (Number.isNaN(d.getTime())) return s
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function yearsSince(s: string): number {
  const start = new Date(s).getTime()
  if (Number.isNaN(start)) return 0
  return Math.max(0, Math.floor((Date.now() - start) / (365.25 * 24 * 3600 * 1000)))
}

function fmt(n: number): string {
  return n % 1 === 0 ? String(n) : n.toFixed(1)
}

const ROLE_LABEL: Record<Contact['role'], string> = {
  primary:     'Primary',
  technical:   'Technical',
  billing:     'Billing',
  stakeholder: 'Stakeholder',
}

const PROJECT_SWATCHES = [
  'oklch(0.6 0.16 250)',  // blue
  'oklch(0.6 0.16 290)',  // purple
  'oklch(0.65 0.16 40)',  // orange
  'oklch(0.6 0.16 145)',  // green
  'oklch(0.55 0.04 240)', // slate
]

// ─── Hero ─────────────────────────────────────────────────────────────────────

function Hero({
  customer, projectCount, contactCount, totalHours,
}: {
  customer: Customer
  projectCount: number
  contactCount: number
  totalHours: number
}) {
  const years = yearsSince(customer.createdAt)
  return (
    <div className="c360-hero">
      <div className="c360-hero-left">
        <div className="c360-logo" style={customerLogoStyle(customer.name)}>
          {initials(customer.name)}
        </div>
        <div className="c360-title">
          <div className="c360-crumbs">
            <Link to="/customers">All customers</Link>
            <span>›</span>
            <span>{customer.country ?? 'Sweden'}</span>
          </div>
          <h1>
            {customer.name}
            <span className={`c360-status ${customer.isActive ? 'active' : 'inactive'}`}>
              {customer.isActive ? 'Active' : 'Inactive'}
            </span>
          </h1>
          <div className="c360-hero-meta">
            <div>
              <Calendar size={13} />
              Customer since <strong>{fmtMonthYear(customer.createdAt)}</strong>
              {years > 0 && <> · {years} {years === 1 ? 'year' : 'years'}</>}
            </div>
            <div>
              <FileText size={13} />
              <strong>{projectCount}</strong> {projectCount === 1 ? 'project' : 'projects'}
            </div>
            <div>
              <UsersIcon size={13} />
              <strong>{contactCount}</strong> {contactCount === 1 ? 'contact' : 'contacts'}
            </div>
            {totalHours > 0 && (
              <div>
                <ShieldCheck size={13} />
                <strong>{fmt(totalHours)}h</strong> logged
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="c360-hero-actions">
        <Link to="/customers" className="btn" style={{ gap: 6, textDecoration: 'none' }}>
          <Pencil size={13} />Edit
        </Link>
      </div>
    </div>
  )
}

// ─── KPIs ─────────────────────────────────────────────────────────────────────

function Kpis({
  totalHours, activeProjects, contactCount, customer,
}: {
  totalHours:     number
  activeProjects: number
  contactCount:   number
  customer:       Customer
}) {
  const legalCount =
    (customer.hasMsa ? 1 : 0) +
    (customer.hasNda ? 1 : 0) +
    (customer.hasDpa ? 1 : 0)

  return (
    <section className="c360-kpis">
      <div className="c360-kpi">
        <div className="c360-kpi-label">Hours logged</div>
        <div className="c360-kpi-val">
          {totalHours > 0 ? fmt(totalHours) : '—'}
          {totalHours > 0 && <span className="unit">h</span>}
        </div>
        <div className="c360-kpi-sub">Across all projects</div>
      </div>
      <div className="c360-kpi">
        <div className="c360-kpi-label">Active projects</div>
        <div className="c360-kpi-val">{activeProjects}</div>
        <div className="c360-kpi-sub">{activeProjects === 0 ? 'No active engagements' : 'In progress'}</div>
      </div>
      <div className="c360-kpi">
        <div className="c360-kpi-label">Contacts</div>
        <div className="c360-kpi-val">{contactCount}</div>
        <div className="c360-kpi-sub">Stakeholders on file</div>
      </div>
      <div className="c360-kpi">
        <div className="c360-kpi-label">Legal coverage</div>
        <div className="c360-kpi-val">
          {legalCount}<span className="unit">/ 3</span>
        </div>
        <div className="c360-legal" style={{ marginTop: 6 }}>
          <span className={`c360-legal-chip ${customer.hasMsa ? 'ok' : 'missing'}`}>MSA</span>
          <span className={`c360-legal-chip ${customer.hasNda ? 'ok' : 'missing'}`}>NDA</span>
          <span className={`c360-legal-chip ${customer.hasDpa ? 'ok' : 'missing'}`}>DPA</span>
        </div>
      </div>
    </section>
  )
}

// ─── Account manager + Properties ────────────────────────────────────────────

function AccountManagerCard({ customer }: { customer: Customer }) {
  return (
    <div className="card">
      <div className="c360-card-head">
        <div className="c360-card-title">Account manager &amp; profile</div>
      </div>
      <div className="c360-card-body">
        {customer.accountManagerName ? (
          <div className="c360-am">
            <div className="c360-am-avatar" style={avatarStyle(customer.accountManagerName)}>
              {initials(customer.accountManagerName)}
            </div>
            <div>
              <div className="c360-am-name">{customer.accountManagerName}</div>
              <div className="c360-am-role">Account manager · Internal</div>
            </div>
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 12.5, paddingBottom: 12 }}>
            No account manager assigned.
          </div>
        )}

        <div className="c360-props">
          {customer.orgNr && (
            <div className="c360-prop">
              <div className="l">Org number</div>
              <div className="v mono">{customer.orgNr}</div>
            </div>
          )}
          {customer.fortnoxCustomerNumber && (
            <div className="c360-prop">
              <div className="l">Fortnox #</div>
              <div className="v mono">{customer.fortnoxCustomerNumber}</div>
            </div>
          )}
          {customer.paymentTerms && (
            <div className="c360-prop">
              <div className="l">Payment terms</div>
              <div className="v">{customer.paymentTerms}</div>
            </div>
          )}
          <div className="c360-prop">
            <div className="l">Currency</div>
            <div className="v">{customer.currency}</div>
          </div>
          {customer.billingEmail && (
            <div className="c360-prop">
              <div className="l">Billing email</div>
              <div className="v">
                <a href={`mailto:${customer.billingEmail}`}>{customer.billingEmail}</a>
              </div>
            </div>
          )}
          {customer.website && (
            <div className="c360-prop">
              <div className="l">Website</div>
              <div className="v">
                <a href={customer.website} target="_blank" rel="noreferrer">
                  {customer.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                </a>
              </div>
            </div>
          )}
          <div className="c360-prop">
            <div className="l">Invoice detail</div>
            <div className="v">{customer.invoiceDetailLevel}</div>
          </div>
          <div className="c360-prop">
            <div className="l">Status</div>
            <div className="v">
              <span className={`c360-status ${customer.isActive ? 'active' : 'inactive'}`}>
                {customer.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        {(customer.devopsOrgUrl || customer.teamsUrl || customer.sharepointUrl) && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--ns-border)' }}>
            <div style={{
              fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.04em', color: 'var(--ns-text-subtle)', marginBottom: 8,
            }}>Linked systems</div>
            {customer.devopsOrgUrl && (
              <a className="c360-link-row" href={customer.devopsOrgUrl} target="_blank" rel="noreferrer">
                <span className="icon" style={{ background: '#0078D4' }}>A</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>Azure DevOps</div>
                  <div style={{ fontSize: 11, color: 'var(--ns-text-subtle)' }} className="mono">
                    {customer.devopsOrgUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </div>
                </div>
                <ExternalLink size={12} />
              </a>
            )}
            {customer.teamsUrl && (
              <a className="c360-link-row" href={customer.teamsUrl} target="_blank" rel="noreferrer">
                <span className="icon" style={{ background: '#6264a7' }}>T</span>
                <div style={{ flex: 1, fontWeight: 500 }}>Teams</div>
                <ExternalLink size={12} />
              </a>
            )}
            {customer.sharepointUrl && (
              <a className="c360-link-row" href={customer.sharepointUrl} target="_blank" rel="noreferrer">
                <span className="icon" style={{ background: '#038387' }}>S</span>
                <div style={{ flex: 1, fontWeight: 500 }}>SharePoint</div>
                <ExternalLink size={12} />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Address card (no map until coordinates exist) ───────────────────────────

function AddressCard({ customer }: { customer: Customer }) {
  const hasAddress = customer.street || customer.city || customer.postalCode
  return (
    <div className="card">
      <div className="c360-card-head">
        <div className="c360-card-title">Headquarters</div>
        {hasAddress && (
          <a
            className="c360-icon-btn"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              [customer.street, customer.postalCode, customer.city, customer.country].filter(Boolean).join(', ')
            )}`}
            target="_blank" rel="noreferrer"
            title="Open in Google Maps"
          >
            <MapPin size={14} />
          </a>
        )}
      </div>
      <div className="c360-card-body">
        {hasAddress ? (
          <div className="c360-address">
            <strong>{customer.name}</strong>
            {customer.street && <>{customer.street}<br /></>}
            {(customer.postalCode || customer.city) && (
              <>{[customer.postalCode, customer.city].filter(Boolean).join(' ')}{customer.country ? `, ${customer.country}` : ''}<br /></>
            )}
            {customer.website && (
              <a href={customer.website} target="_blank" rel="noreferrer"
                 style={{ color: 'var(--ns-brand-strong)', textDecoration: 'none', fontSize: 12 }}>
                <Globe size={11} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                {customer.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </a>
            )}
          </div>
        ) : (
          <div className="muted" style={{ fontSize: 12.5 }}>No address on file.</div>
        )}
      </div>
    </div>
  )
}

// ─── Projects table ──────────────────────────────────────────────────────────

function ProjectsCard({ projects }: { projects: Project[] }) {
  const active = projects.filter(p => p.status === 'active')
  const closed = projects.filter(p => p.status === 'completed' || p.status === 'archived')

  if (projects.length === 0) {
    return (
      <div className="card">
        <div className="c360-card-head">
          <div className="c360-card-title">Projects</div>
        </div>
        <div className="c360-card-body muted" style={{ fontSize: 12.5 }}>
          No projects for this customer yet.
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="c360-card-head">
        <div>
          <div className="c360-card-title">
            Projects · {active.length} active{closed.length > 0 ? `, ${closed.length} closed` : ''}
          </div>
          <div className="c360-card-sub">Click a project to open its 360</div>
        </div>
        <Link to="/projects" className="btn btn-ghost btn-sm" style={{ gap: 4, textDecoration: 'none' }}>
          All projects →
        </Link>
      </div>
      <table className="c360-table">
        <thead>
          <tr>
            <th>Project</th>
            <th>Status</th>
            <th>Manager</th>
            <th className="r">Hours</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p, i) => {
            const swatch = PROJECT_SWATCHES[i % PROJECT_SWATCHES.length]
            const closedTone = p.status !== 'active'
            return (
              <tr key={p.id} className="clickable" onClick={() => { window.location.href = `/projects/${p.id}` }}>
                <td>
                  <div className="c360-proj-cell">
                    <span className="c360-swatch" style={{ background: closedTone ? 'var(--ns-text-subtle)' : swatch }} />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Link to="/projects/$id" params={{ id: String(p.id) }} className="name" onClick={e => e.stopPropagation()}>
                        {p.name}
                      </Link>
                      {p.fortnoxProjectNumber && (
                        <span className="a mono">#{p.fortnoxProjectNumber}</span>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  <span className={`c360-pill ${
                    p.status === 'active' ? 'c360-pill-good' :
                    p.status === 'completed' ? 'c360-pill-neutral' :
                    p.status === 'on_hold' ? 'c360-pill-warn' :
                    'c360-pill-neutral'
                  }`}>
                    {p.status.replace('_', ' ')}
                  </span>
                </td>
                <td style={{ color: closedTone ? 'var(--ns-text-subtle)' : undefined }}>
                  {p.projectManagerName ?? '—'}
                </td>
                <td className="r" style={{ color: closedTone ? 'var(--ns-text-subtle)' : undefined }}>
                  {fmt(p.totalHours)}<span style={{ color: 'var(--ns-text-subtle)', fontSize: 11 }}>h</span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── Contacts list ───────────────────────────────────────────────────────────

function ContactsCard({ contacts }: { contacts: Contact[] }) {
  return (
    <div className="card">
      <div className="c360-card-head">
        <div>
          <div className="c360-card-title">Contacts · {contacts.length}</div>
          <div className="c360-card-sub">Key stakeholders</div>
        </div>
        <Link to="/customers" className="btn btn-ghost btn-sm" style={{ gap: 4, textDecoration: 'none' }}>
          <Plus size={11} />Add
        </Link>
      </div>
      <div className="c360-card-body" style={{ paddingTop: 4 }}>
        {contacts.length === 0 ? (
          <div className="muted" style={{ fontSize: 12.5 }}>No contacts on file.</div>
        ) : (
          <div className="c360-contact-list">
            {contacts.map(c => (
              <div className="c360-contact" key={c.id}>
                <div className="av" style={avatarStyle(c.name)}>{initials(c.name)}</div>
                <div>
                  <div className="name">
                    {c.name}
                    {c.isPrimary && <span className="c360-contact-flag">Primary</span>}
                  </div>
                  <div className="role">
                    {c.title ?? ROLE_LABEL[c.role]}
                    {c.title && <> · {ROLE_LABEL[c.role]}</>}
                  </div>
                </div>
                <div className="icons">
                  {c.email && (
                    <a className="c360-icon-btn" href={`mailto:${c.email}`} aria-label="Email" title={c.email}>
                      <Mail size={13} />
                    </a>
                  )}
                  {c.phone && (
                    <a className="c360-icon-btn" href={`tel:${c.phone}`} aria-label="Phone" title={c.phone}>
                      <Phone size={13} />
                    </a>
                  )}
                  {c.email && (
                    <a className="c360-icon-btn"
                       href={`https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(c.email)}`}
                       target="_blank" rel="noreferrer" aria-label="Chat in Teams">
                      <MessageSquare size={13} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Contracts ───────────────────────────────────────────────────────────────

function ContractsCard({ agreements, customerId }: { agreements: Agreement[]; customerId: number }) {
  const mine = agreements.filter(a => a.customerId === customerId)
  const active     = mine.filter(a => a.status === 'active' || a.status === 'renewal-due')
  const historical = mine.filter(a => a.status === 'expired')

  return (
    <div className="card">
      <div className="c360-card-head">
        <div>
          <div className="c360-card-title">
            Contracts · {mine.length}
          </div>
          <div className="c360-card-sub">
            {active.length} active{historical.length > 0 ? ` · ${historical.length} historical` : ''}
          </div>
        </div>
        <Link to="/contracts" className="btn btn-ghost btn-sm" style={{ gap: 4, textDecoration: 'none' }}>
          All contracts →
        </Link>
      </div>
      {mine.length === 0 ? (
        <div className="c360-card-body muted" style={{ fontSize: 12.5 }}>
          No contracts on file.
        </div>
      ) : (
        <table className="c360-table">
          <thead>
            <tr>
              <th>Contract</th>
              <th>Period</th>
              <th className="r">Value</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {mine.map(a => {
              const muted = a.status !== 'active' && a.status !== 'renewal-due'
              return (
                <tr key={a.id}>
                  <td>
                    <div style={{ fontWeight: 500, color: muted ? 'var(--ns-text-subtle)' : undefined }}>
                      {a.title}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--ns-text-subtle)' }}>
                      {a.contractType.replace('_', ' ')}
                      {a.referenceNumber && <> · <span className="mono">{a.referenceNumber}</span></>}
                    </div>
                  </td>
                  <td style={{ color: muted ? 'var(--ns-text-subtle)' : undefined, fontSize: 12.5 }}>
                    {a.startDate ? fmtMonthYear(a.startDate) : '—'}
                    {' → '}
                    {a.endDate ? fmtMonthYear(a.endDate) : '—'}
                  </td>
                  <td className="r" style={{ color: muted ? 'var(--ns-text-subtle)' : undefined }}>
                    {a.value != null
                      ? `${(a.value / 1000).toFixed(0)}k ${a.currency}`
                      : '—'}
                  </td>
                  <td>
                    <span className={`c360-pill ${
                      a.status === 'active'       ? 'c360-pill-good' :
                      a.status === 'renewal-due'  ? 'c360-pill-warn' :
                      a.status === 'draft'        ? 'c360-pill-info' :
                      'c360-pill-neutral'
                    }`}>
                      {a.status === 'renewal-due' ? 'renewal due' : a.status}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

interface CustomerOverviewPageProps {
  customerId: number
}

export default function CustomerOverviewPage({ customerId }: CustomerOverviewPageProps) {
  const { data: customer, isLoading, isError } = useQuery({
    queryKey: ['customers', customerId, 'detail'],
    queryFn:  () => api.get<CustomerDetail>(`/customers/${customerId}`),
  })

  // All projects + client-side filter — there's no /customers/{id}/projects
  // endpoint, but /projects returns customerId on each row.
  const { data: allProjects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn:  () => api.get<Project[]>('/projects'),
    staleTime: 5 * 60 * 1000,
  })

  const { data: allAgreements = [] } = useQuery({
    queryKey: ['contracts', 'agreements'],
    queryFn:  () => api.get<Agreement[]>('/contracts/agreements'),
  })

  const projects = useMemo(
    () => allProjects.filter(p => p.customerId === customerId)
      .sort((a, b) => {
        // active first, then by name
        if (a.status === 'active' && b.status !== 'active') return -1
        if (a.status !== 'active' && b.status === 'active') return 1
        return a.name.localeCompare(b.name)
      }),
    [allProjects, customerId],
  )

  const totalHours      = projects.reduce((s, p) => s + (p.totalHours ?? 0), 0)
  const activeProjects  = projects.filter(p => p.status === 'active').length
  const customerContracts = useMemo(
    () => allAgreements.filter(a => a.customerId === customerId),
    [allAgreements, customerId],
  )

  if (isLoading) {
    return <div className="page"><div className="muted" style={{ padding: 32, textAlign: 'center', fontSize: 13 }}>Loading…</div></div>
  }
  if (isError || !customer) {
    return <div className="page"><div className="muted" style={{ padding: 32, textAlign: 'center', fontSize: 13 }}>Customer not found.</div></div>
  }

  return (
    <div className="page">
      <Link
        to="/customers"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          fontSize: 12, color: 'var(--ns-text-muted)',
          textDecoration: 'none', marginBottom: 8,
        }}
      >
        <ArrowLeft size={13} />All customers
      </Link>

      <Hero
        customer={customer}
        projectCount={projects.length}
        contactCount={customer.contacts.length}
        totalHours={totalHours}
      />

      {/* Tabs (Overview only for now; others disabled with counts) */}
      <div className="c360-tabs">
        <button className="on">Overview</button>
        <button disabled>Projects<span className="count">{projects.length}</span></button>
        <button disabled>Contracts<span className="count">{customerContracts.length}</span></button>
        <button disabled>Invoices</button>
        <button disabled>Opportunities</button>
        <button disabled>Contacts<span className="count">{customer.contacts.length}</span></button>
        <button disabled>Activity</button>
        <button disabled>Documents</button>
      </div>

      <Kpis
        totalHours={totalHours}
        activeProjects={activeProjects}
        contactCount={customer.contacts.length}
        customer={customer}
      />

      <section className="c360-grid-2-1">
        <ProjectsCard projects={projects} />
        <div className="c360-col-stack">
          <AccountManagerCard customer={customer} />
          <AddressCard customer={customer} />
        </div>
      </section>

      <section className="c360-grid-1-1">
        <ContactsCard contacts={customer.contacts} />
        <ContractsCard agreements={customerContracts} customerId={customerId} />
      </section>

      {customer.notes && (
        <section className="card" style={{ marginBottom: 16 }}>
          <div className="c360-card-head">
            <div className="c360-card-title">Notes</div>
          </div>
          <div className="c360-card-body">
            <p style={{ fontSize: 13, lineHeight: 1.55, margin: 0, whiteSpace: 'pre-wrap' }}>
              {customer.notes}
            </p>
          </div>
        </section>
      )}
    </div>
  )
}
